import React, { useState, useCallback } from 'react';
import { HOLOLAB_COLORS, HOLOLAB_FONTS } from '../../constants';

// ─── Constants ─────────────────────────────────────────────────────────────────

export const TIME_POINTS = [0.0, 0.5, 1.0, 1.5, 2.0, 2.5, 3.0, 3.5, 4.0, 4.5, 5.0];

export const CORRECT_BURETTE: Record<string, number> = {
  '0.0': 50.00, '0.5': 47.20, '1.0': 44.80, '1.5': 42.60, '2.0': 40.80,
  '2.5': 39.40, '3.0': 38.20, '3.5': 37.40, '4.0': 36.80, '4.5': 36.40, '5.0': 36.20,
};

export const CORRECT_GAS: Record<string, number> = Object.fromEntries(
  TIME_POINTS.map((t) => [t.toFixed(1), parseFloat((50 - CORRECT_BURETTE[t.toFixed(1)]).toFixed(2))])
);

const DISCUSSION_CORRECT: Record<number, string> = {
  1: 'Bubbles were observed in the conical flask; the water level in the burette decreased as hydrogen gas was collected.',
  2: 'Hydrogen gas (H₂)',
  3: 'Zn + H₂SO₄ → ZnSO₄ + H₂',
  4: 'The reaction is complete when no more bubbles are produced in the flask and the burette reading stops changing.',
};

const DISCUSSION_QUESTIONS: Record<number, string> = {
  1: 'State the observable changes and the measurement made in the activity.',
  2: 'Name the gas released.',
  3: 'Write a chemical equation for the reaction that occurred.',
  4: 'How would you know that the reaction is completed?',
};

const TOLERANCE = 0.5;

// ─── Types ────────────────────────────────────────────────────────────────────

interface UserInputs { burette: Record<string, string>; gas: Record<string, string>; }
type ValidationResult = { burette: Record<string, boolean | null>; gas: Record<string, boolean | null>; };
interface DiscussionInputs { [key: number]: string; }

interface ExperimentTableProps {
  recordedData: Record<string, number>;
  highlightTime: number | null;
  experimentComplete: boolean;
  onReset: () => void;
  autoFillData?: boolean;
  showDiscussion?: boolean;
  discussionQuestions?: { _id: string; question: string; modelAnswer?: string }[];
  onSaveLabData?: (dataPoints: any[]) => Promise<void>;
  onSubmitDiscussion?: (answers: { questionId: string; answer: string }[]) => Promise<void>;
}

// ─── Cell Input ───────────────────────────────────────────────────────────────

const TableCell: React.FC<{
  timeKey: string; value: string; prefilled: boolean; validation: boolean | null;
  correctValue: number; highlighted: boolean; onChange: (v: string) => void; submitted: boolean;
}> = ({ timeKey, value, prefilled, validation, correctValue, highlighted, onChange, submitted }) => {
  const borderColor = submitted && !prefilled
    ? validation === true ? HOLOLAB_COLORS.successGreen : validation === false ? HOLOLAB_COLORS.errorRed : 'rgba(10,25,41,0.1)'
    : highlighted ? HOLOLAB_COLORS.warningAmber : 'rgba(10,25,41,0.08)';

  const bg = submitted && !prefilled
    ? validation === true ? 'rgba(16,185,129,0.06)' : validation === false ? 'rgba(239,68,68,0.06)' : '#fff'
    : highlighted ? 'rgba(245,158,11,0.06)' : '#fff';

  return (
    <td style={{ padding: '6px 4px', textAlign: 'center', borderBottom: '1px solid rgba(10,25,41,0.06)', background: bg, transition: 'all 250ms', borderLeft: `1px solid ${borderColor}`, borderRight: `1px solid ${borderColor}`, minWidth: '58px' }}>
      {prefilled ? (
        <span style={{ fontFamily: HOLOLAB_FONTS.mono, fontSize: '12px', color: HOLOLAB_COLORS.deepBlue, fontWeight: 600 }}>{value}</span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
          <input
            type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder="—"
            disabled={submitted && validation !== null}
            style={{ width: '50px', textAlign: 'center', border: 'none', outline: 'none', background: 'transparent', fontFamily: HOLOLAB_FONTS.mono, fontSize: '12px', color: HOLOLAB_COLORS.deepBlue, padding: '2px 0' }}
          />
          {submitted && validation === false && (
            <span style={{ fontFamily: HOLOLAB_FONTS.mono, fontSize: '9px', color: HOLOLAB_COLORS.successGreen, opacity: 0.9 }}>
              {correctValue.toFixed(2)}
            </span>
          )}
          {submitted && validation !== null && (
            <span style={{ fontSize: '9px', color: validation ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.errorRed }}>{validation ? '✓' : '✗'}</span>
          )}
        </div>
      )}
    </td>
  );
};

// ─── Discussion Question ───────────────────────────────────────────────────────

const DiscussionItem: React.FC<{
  num: number; question: string; modelAnswer?: string; value: string; submitted: boolean;
  isCorrect: boolean | null; isExpanded: boolean; onToggle: () => void; onChange: (v: string) => void;
}> = ({ num, question, modelAnswer, value, submitted, isCorrect, isExpanded, onToggle, onChange }) => {
  const borderColor = submitted
    ? isCorrect === true ? 'rgba(16,185,129,0.25)' : isCorrect === false ? 'rgba(239,68,68,0.2)' : 'rgba(10,25,41,0.06)'
    : 'rgba(10,25,41,0.06)';
  const bg = submitted
    ? isCorrect === true ? 'rgba(16,185,129,0.03)' : isCorrect === false ? 'rgba(239,68,68,0.03)' : '#fff'
    : '#fff';

  return (
    <div style={{ borderRadius: '8px', border: `1px solid ${borderColor}`, background: bg, overflow: 'hidden', transition: 'all 250ms' }}>
      {/* Header */}
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 12px', border: 'none', background: 'transparent', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600, color: HOLOLAB_COLORS.precisionBlue, flexShrink: 0 }}>
          Q{num}
        </span>
        <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 500, color: HOLOLAB_COLORS.deepBlue, flex: 1, lineHeight: 1.4 }}>
          {question}
        </span>
        {submitted && isCorrect !== null && (
          <span style={{ fontSize: '12px', flexShrink: 0 }}>{isCorrect ? '✅' : '❌'}</span>
        )}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(10,25,41,0.3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transition: 'transform 200ms', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div style={{ padding: '0 12px 12px' }}>
          <textarea
            value={value} onChange={(e) => onChange(e.target.value)} rows={2}
            placeholder="Write your answer here…"
            disabled={submitted}
            style={{
              width: '100%', boxSizing: 'border-box', fontFamily: HOLOLAB_FONTS.body, fontSize: '12px',
              color: HOLOLAB_COLORS.deepBlue, border: '1px solid rgba(10,25,41,0.1)', borderRadius: '6px',
              padding: '8px 10px', resize: 'vertical', outline: 'none', background: '#fafcfd', lineHeight: 1.5,
            }}
          />
          {submitted && (
            <div style={{ marginTop: '8px', padding: '8px 10px', borderRadius: '6px', background: 'rgba(232,244,248,0.7)', border: '1px solid rgba(46,92,138,0.12)' }}>
              <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '10px', color: 'rgba(10,25,41,0.45)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Model answer
              </span>
              <p style={{
                fontFamily: num === 3 ? HOLOLAB_FONTS.mono : HOLOLAB_FONTS.body,
                fontSize: '12px', color: HOLOLAB_COLORS.deepBlue, margin: '4px 0 0', lineHeight: 1.5,
              }}>
                {modelAnswer ?? DISCUSSION_CORRECT[num]}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

const ExperimentTable: React.FC<ExperimentTableProps> = ({
  recordedData, highlightTime, experimentComplete, onReset, autoFillData = false,
  showDiscussion = true, discussionQuestions, onSaveLabData, onSubmitDiscussion
}) => {
  const [userInputs, setUserInputs] = useState<UserInputs>({ burette: { '0.0': '50.00' }, gas: { '0.0': '0.00' } });
  const [submitted, setSubmitted] = useState(false);
  const [validation, setValidation] = useState<ValidationResult>({ burette: {}, gas: {} });
  const [score, setScore] = useState<{ correct: number; total: number } | null>(null);

  const [discussion, setDiscussion] = useState<DiscussionInputs>({ 1: '', 2: '', 3: '', 4: '' });
  const [discussionSubmitted, setDiscussionSubmitted] = useState(false);
  const [discussionResults, setDiscussionResults] = useState<Record<number, boolean | null>>({ 1: null, 2: null, 3: null, 4: null });
  const [expandedQ, setExpandedQ] = useState<number | null>(1);

  const handleBuretteChange = useCallback((timeKey: string, value: string) => {
    setUserInputs((prev) => ({ ...prev, burette: { ...prev.burette, [timeKey]: value } }));
  }, []);

  const handleGasChange = useCallback((timeKey: string, value: string) => {
    setUserInputs((prev) => ({ ...prev, gas: { ...prev.gas, [timeKey]: value } }));
  }, []);

  const handleSubmit = () => {
    const newBuretteValidation: Record<string, boolean | null> = {};
    const newGasValidation: Record<string, boolean | null> = {};
    let correct = 0, total = 0;

    TIME_POINTS.slice(1).forEach((t) => {
      const key = t.toFixed(1);
      const bInput = parseFloat(userInputs.burette[key] ?? '');
      const gInput = parseFloat(userInputs.gas[key] ?? '');

      total++;
      if (!isNaN(bInput)) {
        const isCorrect = Math.abs(bInput - CORRECT_BURETTE[key]) <= TOLERANCE;
        newBuretteValidation[key] = isCorrect;
        if (isCorrect) correct++;
      } else { newBuretteValidation[key] = false; }

      total++;
      if (!isNaN(gInput)) {
        const isCorrect = Math.abs(gInput - CORRECT_GAS[key]) <= TOLERANCE;
        newGasValidation[key] = isCorrect;
        if (isCorrect) correct++;
      } else { newGasValidation[key] = false; }
    });

    setValidation({ burette: newBuretteValidation, gas: newGasValidation });
    setScore({ correct, total });
    setSubmitted(true);

    if (onSaveLabData) {
      // Package the data up for saving
      const dataPoints = TIME_POINTS.map(t => ({
        time: t,
        burette: parseFloat(userInputs.burette[t.toFixed(1)] ?? '0'),
        gas: parseFloat(userInputs.gas[t.toFixed(1)] ?? '0')
      }));
      onSaveLabData(dataPoints).catch(e => console.error("Failed to save lab data", e));
    }
  };

  const handleDiscussionSubmit = () => {
    const results: Record<number, boolean | null> = {};
    [1, 2, 3, 4].forEach((n) => {
      const ans = discussion[n].trim().toLowerCase();
      if (n === 2) results[n] = ans.includes('hydrogen') || ans.includes('h₂') || ans.includes('h2');
      else if (n === 3) results[n] = ans.includes('zn') && (ans.includes('h2so4') || ans.includes('h₂so₄')) && (ans.includes('znso4') || ans.includes('znso₄')) && (ans.includes('h2') || ans.includes('h₂'));
      else results[n] = ans.length >= 10;
    });
    setDiscussionResults(results);
    setDiscussionSubmitted(true);
    setExpandedQ(1);

    if (onSubmitDiscussion && discussionQuestions && discussionQuestions.length > 0) {
      const answers = discussionQuestions.map((q, idx) => ({
        questionId: q._id,
        answer: discussion[idx + 1] || '' // 1-indexed discussion
      })).filter(a => a.questionId); // Only submit if valid questionId
      
      onSubmitDiscussion(answers).catch(e => console.error("Failed to submit discussion", e));
    }
  };

  const handleReset = () => {
    setUserInputs({ burette: { '0.0': '50.00' }, gas: { '0.0': '0.00' } });
    setSubmitted(false); setValidation({ burette: {}, gas: {} }); setScore(null);
    setDiscussion({ 1: '', 2: '', 3: '', 4: '' }); setDiscussionSubmitted(false);
    setDiscussionResults({ 1: null, 2: null, 3: null, 4: null }); setExpandedQ(1);
    onReset();
  };

  const highlightKey = highlightTime !== null ? highlightTime.toFixed(1) : null;
  const answeredCount = Object.values(discussion).filter((v) => v.trim().length > 0).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* ── Table 7.1 ── */}
      <section>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <h3 style={{ fontFamily: HOLOLAB_FONTS.heading, fontSize: '15px', fontWeight: 700, color: HOLOLAB_COLORS.deepBlue, margin: 0 }}>
            Table 7.1 — Results
          </h3>
          {score && (
            <span style={{
              fontFamily: HOLOLAB_FONTS.mono, fontSize: '11px', fontWeight: 600,
              padding: '2px 8px', borderRadius: '10px',
              background: score.correct / score.total >= 0.8 ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
              color: score.correct / score.total >= 0.8 ? HOLOLAB_COLORS.successGreen : HOLOLAB_COLORS.warningAmber,
            }}>
              {score.correct}/{score.total} ({Math.round((score.correct / score.total) * 100)}%)
            </span>
          )}
        </div>

        <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid rgba(10,25,41,0.06)', background: '#fff' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontFamily: HOLOLAB_FONTS.body, fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f7f9fb' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: HOLOLAB_COLORS.deepBlue, fontSize: '11px', borderBottom: '1px solid rgba(10,25,41,0.08)', position: 'sticky', left: 0, background: '#f7f9fb', zIndex: 1, minWidth: '100px' }}>
                  Time (min)
                </th>
                {TIME_POINTS.map((t) => (
                  <th key={t} style={{
                    padding: '8px 4px', textAlign: 'center', fontWeight: 600,
                    color: HOLOLAB_COLORS.deepBlue, fontSize: '11px',
                    borderBottom: '1px solid rgba(10,25,41,0.08)',
                    background: highlightKey === t.toFixed(1) ? 'rgba(245,158,11,0.1)' : '#f7f9fb',
                    transition: 'background 250ms',
                  }}>
                    {t.toFixed(1)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Burette row */}
              <tr>
                <td style={{ padding: '8px 10px', fontWeight: 500, color: HOLOLAB_COLORS.deepBlue, borderBottom: '1px solid rgba(10,25,41,0.06)', whiteSpace: 'nowrap', fontSize: '11px', background: '#fafcfd', position: 'sticky', left: 0, zIndex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    📊 Burette (cm³)
                  </span>
                </td>
                {TIME_POINTS.map((t) => {
                  const key = t.toFixed(1);
                  const autoFillValueBurette = autoFillData && key in recordedData ? recordedData[key].toFixed(2) : undefined;
                  const isPrefilled = t === 0 || autoFillValueBurette !== undefined;
                  return (
                    <TableCell key={key} timeKey={key}
                      value={isPrefilled ? (autoFillValueBurette ?? '50.00') : userInputs.burette[key] ?? ''}
                      prefilled={isPrefilled}
                      validation={submitted ? validation.burette[key] ?? null : null}
                      correctValue={CORRECT_BURETTE[key]}
                      highlighted={highlightKey === key && !submitted}
                      onChange={(v) => handleBuretteChange(key, v)} submitted={submitted}
                    />
                  );
                })}
              </tr>

              {/* Gas volume row */}
              <tr>
                <td style={{ padding: '8px 10px', fontWeight: 500, color: HOLOLAB_COLORS.deepBlue, borderBottom: '1px solid rgba(10,25,41,0.06)', whiteSpace: 'nowrap', fontSize: '11px', background: '#fafcfd', position: 'sticky', left: 0, zIndex: 1 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    🧪 Gas vol (cm³)
                  </span>
                </td>
                {TIME_POINTS.map((t) => {
                  const key = t.toFixed(1);
                  const autoFillValueGas = autoFillData && key in recordedData ? (50 - recordedData[key]).toFixed(2) : undefined;
                  const isPrefilled = t === 0 || autoFillValueGas !== undefined;
                  return (
                    <TableCell key={key} timeKey={key}
                      value={isPrefilled ? (autoFillValueGas ?? '0.00') : userInputs.gas[key] ?? ''}
                      prefilled={isPrefilled}
                      validation={submitted ? validation.gas[key] ?? null : null}
                      correctValue={CORRECT_GAS[key]}
                      highlighted={highlightKey === key && !submitted}
                      onChange={(v) => handleGasChange(key, v)} submitted={submitted}
                    />
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Submit / Reset */}
        <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          {!experimentComplete && !submitted && (
            <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', color: 'rgba(10,25,41,0.4)' }}>
              Complete the experiment to unlock submission.
            </span>
          )}
          {submitted && <div />}
          <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
            {submitted && (
              <button onClick={handleReset} style={{
                background: 'transparent', color: HOLOLAB_COLORS.precisionBlue,
                border: `1px solid ${HOLOLAB_COLORS.precisionBlue}`, borderRadius: '8px', padding: '7px 16px',
                fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600, cursor: 'pointer', transition: 'all 150ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(46,92,138,0.04)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                Reset
              </button>
            )}
            {!submitted && (
              <button onClick={handleSubmit} disabled={!experimentComplete} style={{
                background: experimentComplete ? HOLOLAB_COLORS.precisionBlue : 'rgba(10,25,41,0.08)',
                color: experimentComplete ? '#fff' : 'rgba(10,25,41,0.3)',
                border: 'none', borderRadius: '8px', padding: '7px 18px',
                fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600,
                cursor: experimentComplete ? 'pointer' : 'not-allowed', transition: 'all 150ms',
                boxShadow: experimentComplete ? '0 1px 4px rgba(46,92,138,0.15)' : 'none',
              }}>
                Submit Answers
              </button>
            )}
          </div>
        </div>
      </section>

      {/* ── Discussion Questions ── */}
      {showDiscussion && (
        <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <h3 style={{ fontFamily: HOLOLAB_FONTS.heading, fontSize: '15px', fontWeight: 700, color: HOLOLAB_COLORS.deepBlue, margin: 0 }}>
            Discussion
          </h3>
          <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '11px', color: 'rgba(10,25,41,0.4)' }}>
            {answeredCount}/4 answered
          </span>
        </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {
              // Use server-provided discussionQuestions if available, otherwise fall back to built-in questions
              ((discussionQuestions && discussionQuestions.length > 0)
                ? discussionQuestions.map((q, idx) => ({ question: q.question, modelAnswer: q.modelAnswer ?? undefined }))
                : [1,2,3,4].map((n) => ({ question: DISCUSSION_QUESTIONS[n], modelAnswer: DISCUSSION_CORRECT[n] })))
              .map((q, idx) => (
                <DiscussionItem
                  key={idx} num={idx+1}
                  question={q.question}
                  modelAnswer={q.modelAnswer}
                  value={discussion[idx+1]}
                  submitted={discussionSubmitted}
                  isCorrect={discussionResults[idx+1]}
                  isExpanded={expandedQ === idx+1}
                  onToggle={() => setExpandedQ(expandedQ === idx+1 ? null : idx+1)}
                  onChange={(v) => setDiscussion((p) => ({ ...p, [idx+1]: v }))}
                />
              ))
            }
          </div>

        <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'flex-end' }}>
          {!discussionSubmitted ? (
            <button onClick={handleDiscussionSubmit} style={{
              background: HOLOLAB_COLORS.precisionBlue, color: '#fff',
              border: 'none', borderRadius: '8px', padding: '7px 18px',
              fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600,
              cursor: 'pointer', transition: 'all 150ms',
              boxShadow: '0 1px 4px rgba(46,92,138,0.15)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}>
              Submit Discussion
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 14px', borderRadius: '8px', background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.2)' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={HOLOLAB_COLORS.successGreen} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
              <span style={{ fontFamily: HOLOLAB_FONTS.body, fontSize: '12px', fontWeight: 600, color: HOLOLAB_COLORS.successGreen }}>
                Submitted — check model answers above
              </span>
            </div>
          )}
        </div>
        </section>
      )}
    </div>
  );
};

export default ExperimentTable;
